/**
 * TD — Tier-1 KYC review queue (web, coop-admin / admin).
 *
 * Coop-admin (or admin) lands here from the sidebar and sees every KYC
 * submission with fica_status='submitted' that they're allowed to review
 * (the server scopes coop_admin to farmers in cooperatives they admin).
 * Each card shows the farmer's name + phone + FICA tier + cooperative +
 * submission time, plus [Approve] / [Reject] buttons. Both decisions route
 * through a confirm Dialog carrying an optional reviewer note, then PATCH
 * `/kyc/:userId` with { decision, reviewerNote }.
 *
 * On success the ['kyc-pending'] list is invalidated (so the reviewed row
 * drops) and ['farmers'] too (so any roster KYC badge redraws). If the row
 * was already reviewed elsewhere the server returns 400 not_pending — that
 * FseApiError message surfaces inside the dialog.
 *
 * Mirrors `_app.pending-reviews.tsx`. The farmer-
 * facing `/verification` screen is separate and untouched.
 */
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  Chip,
  Text,
  TextField,
} from '@fse/ui';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';

import { FseApiError } from '../api/client';
import {
  kycControllerListPending,
  kycControllerReview,
} from '../api/generated/kyc/kyc';
import {
  ReviewKycDtoDecision,
  type KycPendingItemDto,
} from '../api/generated/models';

const dateFormatter = new Intl.DateTimeFormat(undefined, {
  year: 'numeric',
  month: 'short',
  day: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

/** Format an ISO-8601 submission timestamp to a readable local date-time,
 *  guarding NaN by falling back to the raw string. */
function formatSubmittedAt(iso: string): string {
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return iso;
  return dateFormatter.format(parsed);
}

/** Normalise an error (FseApiError → its server `message`, else stringified). */
function errMessage(error: unknown): string {
  return error instanceof FseApiError ? error.message : String(error);
}

export function KycReviewsScreen() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const listQuery = useQuery({
    queryKey: ['kyc-pending'],
    queryFn: ({ signal }) => kycControllerListPending(signal),
  });

  function refresh() {
    queryClient.invalidateQueries({ queryKey: ['kyc-pending'] });
    // The farmers roster surfaces a KYC badge — refresh it so the badge moves
    // in lockstep with the decision taken here.
    queryClient.invalidateQueries({ queryKey: ['farmers'] });
  }

  const items = listQuery.data?.data ?? [];
  const count = listQuery.data?.data.length;

  return (
    <Stack spacing={3}>
      <Box>
        <Text variant="headline-medium" as="h2">
          {t('kycReviews.title')}
        </Text>
        <Box sx={{ mt: 0.5 }}>
          <Text variant="body-medium" color="text.secondary">
            {typeof count === 'number'
              ? t('kycReviews.subtitleWithCount', { count })
              : t('kycReviews.subtitle')}
          </Text>
        </Box>
      </Box>

      {listQuery.isPending ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : listQuery.isError ? (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => listQuery.refetch()}>
              {t('kycReviews.retry')}
            </Button>
          }
        >
          {t('kycReviews.loadError', { error: errMessage(listQuery.error) })}
        </Alert>
      ) : items.length === 0 ? (
        <Card>
          <CardHeader title={t('kycReviews.empty.title')} />
          <CardContent>
            <Text variant="body-medium" color="text.secondary">
              {t('kycReviews.empty.body')}
            </Text>
          </CardContent>
        </Card>
      ) : (
        <Stack spacing={2}>
          {items.map((item) => (
            <KycReviewCard key={item.userId} item={item} onReviewed={refresh} t={t} />
          ))}
        </Stack>
      )}
    </Stack>
  );
}

interface KycReviewCardProps {
  item: KycPendingItemDto;
  onReviewed: () => void;
  t: TFunction;
}

function KycReviewCard({ item, onReviewed, t }: KycReviewCardProps) {
  // Both decisions go through a confirm Dialog so the reviewer can attach a
  // note before committing. `pending` holds which decision the dialog is
  // confirming (null = closed).
  const [pending, setPending] = useState<ReviewKycDtoDecision | null>(null);
  const [reviewerNote, setReviewerNote] = useState('');

  const reviewMutation = useMutation({
    mutationFn: (decision: ReviewKycDtoDecision) =>
      kycControllerReview(item.userId, {
        decision,
        reviewerNote: reviewerNote.trim() ? reviewerNote.trim() : undefined,
      }),
    onSuccess: () => {
      closeDialog();
      onReviewed();
    },
  });

  function openDialog(decision: ReviewKycDtoDecision) {
    reviewMutation.reset();
    setReviewerNote('');
    setPending(decision);
  }

  function closeDialog() {
    if (reviewMutation.isPending) return;
    setPending(null);
  }

  const tierLabel =
    item.ficaTier == null
      ? t('kycReviews.card.tierNone')
      : t('kycReviews.card.tier', { tier: item.ficaTier });

  const isReject = pending === ReviewKycDtoDecision.rejected;

  return (
    <Card variant="outlined">
      <CardHeader
        title={
          <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
            <Text variant="title-medium" as="span">
              {item.displayName}
            </Text>
            <Chip kind="input" color="neutral" label={item.phoneE164} />
            <Chip kind="assist" color="tertiary" label={tierLabel} />
          </Stack>
        }
        subheader={
          <Text variant="label-medium" color="text.secondary">
            {t('kycReviews.card.submitted', { date: formatSubmittedAt(item.submittedAt) })}
          </Text>
        }
      />
      <CardContent>
        <Stack spacing={2}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: '120px 1fr',
              alignItems: 'baseline',
              gap: 1,
            }}
          >
            <Text variant="body-medium" color="text.secondary">
              {t('kycReviews.card.coopLabel')}
            </Text>
            <Text variant="body-medium" color="text.primary">
              {item.cooperativeName ?? '—'}
            </Text>
          </Box>

          <Stack direction="row" spacing={1.5} sx={{ justifyContent: 'flex-end' }}>
            <Button
              variant="outlined"
              color="error"
              disabled={reviewMutation.isPending}
              onClick={() => openDialog(ReviewKycDtoDecision.rejected)}
            >
              {t('kycReviews.card.reject')}
            </Button>
            <Button
              variant="contained"
              disabled={reviewMutation.isPending}
              onClick={() => openDialog(ReviewKycDtoDecision.verified)}
            >
              {t('kycReviews.card.approve')}
            </Button>
          </Stack>
        </Stack>
      </CardContent>

      <Dialog open={pending !== null} onClose={closeDialog} maxWidth="xs" fullWidth>
        <DialogTitle>
          {isReject
            ? t('kycReviews.confirm.rejectTitle')
            : t('kycReviews.confirm.approveTitle')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {isReject
              ? t('kycReviews.confirm.rejectBody', { name: item.displayName })
              : t('kycReviews.confirm.approveBody', { name: item.displayName })}
          </DialogContentText>
          <TextField
            label={t('kycReviews.confirm.noteLabel')}
            value={reviewerNote}
            onChange={(e) => setReviewerNote(e.target.value)}
            multiline
            minRows={1}
            maxRows={4}
            fullWidth
            disabled={reviewMutation.isPending}
            sx={{ mt: 2 }}
          />
          {reviewMutation.isError ? (
            <Alert severity="error" sx={{ mt: 2 }}>
              {t('kycReviews.confirm.error', { error: errMessage(reviewMutation.error) })}
            </Alert>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button variant="text" disabled={reviewMutation.isPending} onClick={closeDialog}>
            {t('kycReviews.confirm.cancel')}
          </Button>
          <Button
            variant="contained"
            color={isReject ? 'error' : 'primary'}
            disabled={reviewMutation.isPending}
            startIcon={reviewMutation.isPending ? <CircularProgress size={16} /> : undefined}
            onClick={() => pending && reviewMutation.mutate(pending)}
          >
            {isReject
              ? t('kycReviews.confirm.confirmReject')
              : t('kycReviews.confirm.confirmApprove')}
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
}
